import './styles.scss';

import { Box } from '@mui/material';
import * as echarts from 'echarts';
import { useEffect, useRef } from 'react';

import { useResizeContainer } from '../../../hooks/useResizeContainer';
import theme from './theme';
import { useOptions } from './hooks/use-options';
import { use3DOptions } from './hooks/use-3d-options';

import classNames from 'classnames';

interface IProps {
  series?: any;
  xAxis?: any & {
    formatter?: (value: any) => string;
    name?: string;
  };
  yAxis?: any & {
    axisPointer?: { label: { margin: number } };
    axisLabel?: { formatter: (value: any) => string };
    name?: string;
  };
  tooltip?: echarts.TooltipComponentOption & {
    xAxisTitle?: string;
    yAxisTitle?: string;
    zAxisTitle?: string;
    valueFormatter?: (value: any) => string;
    xAxisFormatter?: (value: any) => string;
    yAxisFormatter?: (value: any) => string;
    zAxisFormatter?: (value: any) => string;
  };
  className?: string;
  merge?: boolean | true;
  isLoading?: boolean;
  onLegendSelectChanged?: (payload: { name: string; selected: Record<string, boolean> }) => void;

  is3D?: boolean;
  data3D?: any[][];
  xAxisName?: string;
  yAxisName?: string;
  zAxisName?: string;
  min3D?: number;
  max3D?: number;
  showWireframe?: boolean;
  xAxisData?: string[] | number[];
  yAxisData?: string[] | number[];
  legend?: echarts.LegendComponentOption;
  grid?: any;
  visualMap?: any;
  useBoundaryGap?: boolean;
  useGL?: boolean;
}

import CircularProgress from '@mui/material/CircularProgress';

const Chart = ({
  className = '',
  series = [],
  xAxis = {},
  yAxis = {},
  merge,
  tooltip,
  isLoading = false,
  onLegendSelectChanged,

  is3D = false,
  data3D = [],
  xAxisName,
  yAxisName,
  zAxisName,
  min3D = 10,
  max3D = 80,
  showWireframe = true,
  xAxisData = [],
  yAxisData = [],
  legend,
  grid,
  visualMap,
  useBoundaryGap,
  useGL,
}: IProps) => {
  const chartRef = useRef<any>();
  const myChart = useRef<any>();

  const {
    containerRef: chartContainerRef,
    containerSize,
    dispatchResize,
  } = useResizeContainer([400, 300], 0, 0);

  const option2D = useOptions({
    series,
    xAxis,
    yAxis,
    legend,
    grid,
    tooltip: {
      ...tooltip,
    },
    visualMap,
    boundaryGap: useBoundaryGap,
  });

  const option3D = use3DOptions({
    data: data3D,
    xAxisName,
    yAxisName,
    zAxisName,
    min: min3D,
    max: max3D,
    showWireframe,
    tooltip,
    xAxisData,
    yAxisData,
    visualMap,
  });

  const option = is3D ? option3D : option2D;

  const ensureCategoryGap = (ax: any) => {
    if (Array.isArray(ax)) {
      return ax.map((a) => (a?.type === 'category' ? { ...a, boundaryGap: true } : a));
    }
    return ax?.type === 'category' ? { ...ax, boundaryGap: true } : ax;
  };

  useEffect(() => {
    if (isLoading || !myChart.current) return;

    const isHeatmapLike =
      Array.isArray(series) && series.some((s) => s.type === 'heatmap' || s.type === 'custom');
    const patched = isHeatmapLike
      ? {
          ...option,
          axisPointer: { show: false },
          xAxis: ensureCategoryGap(option.xAxis),
          yAxis: ensureCategoryGap(option.yAxis),
          visualMap,
          tooltip: { ...(option as any).tooltip, trigger: 'item', valueFormatter: undefined },
        }
      : option;

    const notMerge = merge === false;

    if (notMerge) {
      let nextLegend = (patched as any).legend ?? {};
      try {
        const prevOpt: any = myChart.current.getOption?.();
        const prevSelected = prevOpt?.legend?.[0]?.selected;
        if (prevSelected && nextLegend && nextLegend.selected == null) {
          nextLegend = { ...nextLegend, selected: prevSelected };
        }
      } catch {
        // noop
      }
      const finalPatched = { ...(patched as any), legend: nextLegend, replaceMerge: ['series'] };

      (myChart.current as any).setOption(finalPatched, true, false);
    } else {
      (myChart.current as any).setOption(patched, false, true);
    }
  }, [option, merge, isLoading, series]);

  useEffect(() => {
    const initChart = async () => {
      if (myChart.current || !chartRef.current) {
        return;
      }
      if (useGL) {
        await import('echarts-gl');
      }

      myChart.current = echarts.init(chartRef.current, theme);
    };
    initChart();
  }, [series, xAxis, yAxis, useGL]);

  useEffect(() => {
    if (!myChart.current || !onLegendSelectChanged) return;

    const handler = (e: any) => {
      try {
        onLegendSelectChanged({ name: e?.name, selected: e?.selected || {} });
      } catch {
        /* noop */
      }
    };

    myChart.current.on('legendselectchanged', handler);
    return () => {
      try {
        myChart.current?.off('legendselectchanged', handler);
      } catch {
        /* noop */
      }
    };
  }, [onLegendSelectChanged]);

  useEffect(() => {
    dispatchResize();
  }, [dispatchResize]);

  useEffect(() => {
    if (!myChart.current) {
      return;
    }

    myChart.current.resize();
  }, [containerSize]);

  const renderIsLoading = () => {
    if (!isLoading) return null;

    return <div className="is-loading-container">{<CircularProgress size={24} />}</div>;
  };

  const renderChart = () => {
    return (
      <Box
        ref={chartRef}
        width={containerSize[0]}
        height={containerSize[1]}
        className={classNames(className, 'chart-container', isLoading ? 'is-loading-box' : '')}
      />
    );
  };

  return (
    <div className={classNames(className, 'chart-container-box')} ref={chartContainerRef}>
      <div className="watermark" />
      {renderIsLoading()}
      {renderChart()}
    </div>
  );
};

export default Chart;
